import React, { useState, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
    ArrowLeft,
    CheckCircle2,
    Activity,
    Trash2,
    FileText,
    Link as LinkIcon,
    Image as ImageIcon,
    Download,
    X,
    Edit2,
    Share2,
    Copy,
    Plus,
    MoreVertical,
    Clock,
    Paperclip,
    Check,
    Send,
    AlertCircle,
    File,
    StickyNote,
    Save,
    Upload,
    Filter,
    FolderOpen,
    Settings,
    Users,
    Archive,
    Info,
    PauseCircle,
    Eye,
    Mail,
    Rocket,
    Sparkles,
    ChevronRight
} from 'lucide-react';
import { Project, Milestone, MilestoneStatus, UserRole, ProjectStatus, TeamMember, AssigneeVisibility, OnboardingQuestion, PresenceStatus, Attachment, Comment, Note } from '../types';
import { Button, StatusBadge, Modal, Input, TextArea, Switch } from './UIComponents';
import { sendProjectInviteEmail } from '../services/emailService';
import { generateMilestones } from '../services/geminiService';

interface ProjectViewProps {
    project: Project;
    role: UserRole;
    teamMembers: TeamMember[];
    onBack: () => void;
    onUpdateMilestone: (projectId: string, milestoneId: string, status: MilestoneStatus) => void;
    onAssignMilestone: (projectId: string, milestoneId: string, assigneeId: string | undefined) => void;
    onInviteMember: (email: string) => string;
    onUpdateProjectStatus: (projectId: string, status: ProjectStatus) => void;
    onUpdateAssigneeVisibility: (projectId: string, visibility: AssigneeVisibility) => void;
    onUpdateProjectQuestions: (projectId: string, questions: OnboardingQuestion[]) => void;
    onUpdateMilestoneTitle: (projectId: string, milestoneId: string, title: string) => void;
    onAddComment: (projectId: string, milestoneId: string, text: string, attachments?: Attachment[], isDelivery?: boolean) => void;
    onResolveComment: (projectId: string, milestoneId: string, commentId: string) => void;
    onEditComment?: (projectId: string, milestoneId: string, commentId: string, text: string) => void;
    onDeleteComment?: (projectId: string, milestoneId: string, commentId: string) => void;
    onDeleteProject: (projectId: string) => void;
    onArchiveProject: (projectId: string) => void;
    onRestoreProject: (projectId: string) => void;
    onOpenReceiverView?: () => void;
    onUpdateMilestoneDuration?: (projectId: string, milestoneId: string, duration: number) => void;
    onDeliverWork?: (projectId: string, milestoneId: string, text: string, attachments: Attachment[]) => void;
    onApproveMilestone?: (projectId: string, milestoneId: string) => void;
    // Notes & Files
    onAddNote?: (projectId: string, title: string, content: string) => void;
    onUpdateNote?: (projectId: string, noteId: string, title: string, content: string) => void;
    onDeleteNote?: (projectId: string, noteId: string) => void;
    onAddProjectFile?: (projectId: string, file: Attachment) => void;
    onFinalizeProject?: () => void;
    onUpdateProject?: (projectId: string, data: Partial<Project>) => void;
    onCompleteDraft?: (projectId: string) => void;
}

// --- Icons ---
const getFileIcon = (type: string) => {
    switch (type) {
        case 'image': return <ImageIcon size={14} strokeWidth={1.5} />;
        case 'link': return <LinkIcon size={14} strokeWidth={1.5} />;
        default: return <FileText size={14} strokeWidth={1.5} />;
    }
}

// --- Sub-components ---

interface AssigneeSelectorProps {
    members: TeamMember[];
    currentAssigneeId?: string;
    onSelect: (id?: string) => void;
    onClose: () => void;
    onInvite?: (email: string) => string;
}

const AssigneeSelector: React.FC<AssigneeSelectorProps> = ({
    members,
    currentAssigneeId,
    onSelect,
    onClose,
    onInvite
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, []);

    const isEmail = searchTerm.includes('@') && searchTerm.length > 5;
    const filteredMembers = members.filter(m =>
        m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (m.email && m.email.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            if (isEmail && onInvite) {
                // Invite
                const newId = onInvite(searchTerm);
                if (newId) onSelect(newId);
                onClose();
            } else if (filteredMembers.length > 0) {
                // Select first match if not explicitly inviting
                onSelect(filteredMembers[0].id);
                onClose();
            }
        }
    }

    return (
        <div className="absolute top-7 left-0 z-50 w-72 bg-zinc-950 border border-zinc-800 rounded-xl shadow-2xl animate-fade-in flex flex-col overflow-hidden ring-1 ring-black/5">
            <div className="p-2 border-b border-zinc-900 bg-zinc-900/30">
                <div className="px-2 py-1.5 text-[10px] text-zinc-500 font-semibold uppercase tracking-wider mb-1">Assign to</div>
                <div className="relative">
                    <Mail size={13} className="absolute left-3 top-2.5 text-zinc-500 pointer-events-none" />
                    <input
                        ref={inputRef}
                        className="w-full bg-zinc-900/50 text-[13px] text-zinc-200 border border-zinc-800 rounded-lg pl-9 pr-3 py-2 focus:outline-none focus:border-zinc-700 focus:bg-zinc-900 placeholder-zinc-600 transition-all"
                        placeholder="Search name or invite email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={handleKeyDown}
                    />
                </div>
            </div>

            <div className="max-h-[280px] overflow-y-auto p-1">
                {/* Invite Action */}
                {isEmail && onInvite && (
                    <button
                        onClick={() => {
                            const newId = onInvite(searchTerm);
                            if (newId) onSelect(newId);
                            onClose();
                        }}
                        className="w-full text-left px-2 py-2 text-[13px] text-zinc-200 hover:bg-zinc-900 rounded-lg flex items-center gap-3 transition-colors mb-1 group"
                    >
                        <div className="w-8 h-8 rounded-full bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400 group-hover:bg-indigo-500/20 transition-colors">
                            <Plus size={14} />
                        </div>
                        <div className="flex flex-col">
                            <span className="font-medium text-indigo-400">Invite "{searchTerm}"</span>
                            <span className="text-[11px] text-zinc-500">Send email invitation</span>
                        </div>
                    </button>
                )}

                {/* Unassigned Option */}
                {(!searchTerm || "unassigned".includes(searchTerm.toLowerCase())) && (
                    <button
                        onClick={() => { onSelect(undefined); onClose(); }}
                        className="w-full text-left px-2 py-2 text-[13px] text-zinc-400 hover:text-zinc-200 hover:bg-zinc-900 rounded-lg flex items-center gap-3 transition-colors"
                    >
                        <div className="w-8 h-8 rounded-full border border-dashed border-zinc-700 flex items-center justify-center text-zinc-600">
                            <X size={14} />
                        </div>
                        <span>Unassigned</span>
                        {!currentAssigneeId && <Check size={14} className="ml-auto text-zinc-500" />}
                    </button>
                )}

                {/* List of Members */}
                {filteredMembers.map(m => (
                    <button
                        key={m.id}
                        onClick={() => { onSelect(m.id); onClose(); }}
                        className={`w-full text-left px-2 py-2 text-[13px] rounded-lg flex items-center gap-3 transition-colors ${currentAssigneeId === m.id ? 'bg-zinc-900 text-zinc-200' : 'text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200'}`}
                    >
                        <div className={`w-8 h-8 rounded-full ${m.avatarColor} flex items-center justify-center text-[10px] text-white font-medium shadow-sm`}>{m.initials}</div>
                        <div className="flex flex-col min-w-0">
                            <span className={`truncate ${currentAssigneeId === m.id ? 'font-medium' : ''}`}>{m.name}</span>
                            <span className="text-[10px] text-zinc-600 truncate">{m.email}</span>
                        </div>
                        {currentAssigneeId === m.id && <Check size={14} className="ml-auto text-emerald-500" />}
                    </button>
                ))}

                {filteredMembers.length === 0 && !isEmail && searchTerm && (
                    <div className="px-4 py-8 text-center">
                        <p className="text-[12px] text-zinc-500 mb-1">No team members found.</p>
                        <p className="text-[11px] text-zinc-600">Type an email address to invite someone new.</p>
                    </div>
                )}
            </div>
        </div>
    )
}

interface CommentItemProps {
    comment: Comment;
    userRole: UserRole;
    onEdit: (id: string, text: string) => void;
    onDelete: (id: string) => void;
}

const CommentItem: React.FC<CommentItemProps> = ({
    comment,
    userRole,
    onEdit,
    onDelete
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editText, setEditText] = useState(comment.text);
    const isAuthor = (userRole === UserRole.PROVIDER && comment.role === UserRole.PROVIDER) || (userRole === UserRole.RECEIVER && comment.role === UserRole.RECEIVER);
    const canModify = isAuthor && !comment.isDeleted;

    if (comment.isDeleted) {
        return (
            <div className="pl-2 py-1 text-[11px] text-zinc-600 italic border-l border-zinc-800 ml-1">
                Deleted
            </div>
        );
    }

    // Deliveries look different - stronger styling, boxy, authoritative
    if (comment.isDelivery) {
        return (
            <div className="relative border border-zinc-800 bg-zinc-900/50 rounded-lg p-4 mb-3 animate-fade-in group">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1.5 text-[10px] bg-rose-500/10 text-rose-400 px-2 py-0.5 rounded border border-rose-500/20 font-medium uppercase tracking-wide">
                            <CheckCircle2 size={11} /> Delivery
                        </div>
                        <span className="text-[11px] text-zinc-500">
                            {new Date(comment.date).toLocaleDateString()}
                        </span>
                    </div>
                    {canModify && (
                        <button onClick={() => onDelete(comment.id)} className="text-zinc-600 hover:text-rose-400 transition-colors"><Trash2 size={12} /></button>
                    )}
                </div>

                <div className="text-[13px] text-zinc-300 leading-relaxed whitespace-pre-wrap mb-4">
                    {comment.text}
                </div>

                {comment.attachments && comment.attachments.length > 0 && (
                    <div className="grid grid-cols-1 gap-2">
                        {comment.attachments.map(att => (
                            <div key={att.id} className="flex items-center gap-3 bg-black border border-zinc-800 rounded px-3 py-2.5 hover:bg-zinc-900 transition-colors cursor-pointer group/att">
                                <div className="w-8 h-8 rounded bg-zinc-900 flex items-center justify-center text-zinc-500 group-hover/att:text-zinc-200 transition-colors border border-zinc-800">
                                    {getFileIcon(att.type)}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="truncate text-[12px] text-zinc-200 font-medium">{att.name}</div>
                                    <div className="text-[10px] text-zinc-600">{att.size || 'Unknown size'}</div>
                                </div>
                                <Download size={14} strokeWidth={1.5} className="text-zinc-600 group-hover/att:text-zinc-200" />
                            </div>
                        ))}
                    </div>
                )}
            </div>
        )
    }

    return (
        <div className={`group relative p-3 rounded-lg transition-all mb-1 hover:bg-zinc-900`}>
            <div className="flex items-start gap-3">
                <div className={`mt-0.5 w-5 h-5 rounded flex items-center justify-center text-[9px] font-bold border ${comment.role === UserRole.PROVIDER ? 'bg-zinc-950 border-zinc-800 text-zinc-400' : 'bg-blue-500/10 border-blue-500/20 text-blue-400'}`}>
                    {comment.author.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between flex-wrap">
                        <span className={`text-[12px] font-medium ${comment.role === UserRole.PROVIDER ? 'text-zinc-200' : 'text-blue-400'}`}>
                            {comment.author}
                        </span>
                        <span className="text-[10px] text-zinc-600 font-mono">
                            {new Date(comment.date).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                        </span>
                    </div>
                    {isEditing ? (
                        <div className="mt-2">
                            <textarea
                                className="w-full bg-black border border-zinc-800 text-[13px] text-zinc-200 p-2 rounded focus:outline-none focus:border-zinc-600 min-h-[60px]"
                                value={editText}
                                onChange={(e) => setEditText(e.target.value)}
                            />
                            <div className="flex justify-end gap-2 mt-2">
                                <button onClick={() => setIsEditing(false)} className="text-[11px] text-zinc-500 hover:text-zinc-300">Cancel</button>
                                <Button size="sm" onClick={() => { onEdit(comment.id, editText); setIsEditing(false); }}>Save</Button>
                            </div>
                        </div>
                    ) : (
                        <div className="text-[13px] text-zinc-400 leading-relaxed whitespace-pre-wrap mt-0.5 break-words">
                            {comment.text}
                        </div>
                    )}

                    {/* Standard comment attachments */}
                    {comment.attachments && comment.attachments.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                            {comment.attachments.map(att => (
                                <div key={att.id} className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 px-2 py-1 rounded text-[11px] text-zinc-500 max-w-full">
                                    <Paperclip size={10} className="shrink-0" />
                                    <span className="truncate">{att.name}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {canModify && !isEditing && (
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 absolute top-3 right-2 bg-zinc-900 rounded shadow-sm">
                        <button onClick={() => setIsEditing(true)} className="p-1 text-zinc-600 hover:text-zinc-200"><Edit2 size={10} /></button>
                        <button onClick={() => onDelete(comment.id)} className="p-1 text-zinc-600 hover:text-rose-400"><Trash2 size={10} /></button>
                    </div>
                )}
            </div>
        </div>
    )
}

interface MilestoneTimelineItemProps {
    milestone: Milestone;
    project: Project;
    role: UserRole;
    teamMembers: TeamMember[];
    onStatusChange: (status: MilestoneStatus) => void;
    onAssign: (id?: string) => void;
    onComment: (text: string, att?: Attachment[], isDelivery?: boolean) => void;
    onEditComment: (cid: string, text: string) => void;
    onDeleteComment: (cid: string) => void;
    onDeliver?: (text: string, attachments: Attachment[]) => void;
    onApprove?: () => void;
    expectedDate?: string;
    onInvite?: (email: string) => string;
}

const MilestoneTimelineItem: React.FC<MilestoneTimelineItemProps> = ({
    milestone,
    project,
    role,
    teamMembers,
    onStatusChange,
    onAssign,
    onComment,
    onEditComment,
    onDeleteComment,
    onDeliver,
    onApprove,
    expectedDate,
    onInvite
}) => {
    const isProvider = role === UserRole.PROVIDER;
    const isReceiver = role === UserRole.RECEIVER;

    const [commentText, setCommentText] = useState('');
    const [isDeliveryMode, setIsDeliveryMode] = useState(false);
    const [showAssigneeSelector, setShowAssigneeSelector] = useState(false);
    const [pendingAttachments, setPendingAttachments] = useState<Attachment[]>([]);

    const fileInputRef = useRef<HTMLInputElement>(null);

    let visibleAssignee: TeamMember | undefined;
    const internalAssignee = teamMembers.find(t => t.id === milestone.assigneeId);
    if (isProvider) {
        visibleAssignee = internalAssignee;
    } else {
        if (project.assigneeVisibility === 'MILESTONE') visibleAssignee = internalAssignee;
        else if (project.assigneeVisibility === 'LEAD') visibleAssignee = teamMembers.find(t => t.role === 'Lead Designer');
        else visibleAssignee = undefined;
    }

    const handleSubmitComment = () => {
        if (!commentText.trim() && pendingAttachments.length === 0) return;

        if (isDeliveryMode && onDeliver) {
            if (pendingAttachments.length === 0 && !window.confirm("Delivering without attachments?")) {
                return;
            }
            onDeliver(commentText, pendingAttachments);
        } else {
            onComment(commentText, pendingAttachments, false);
        }

        setCommentText('');
        setPendingAttachments([]);
        setIsDeliveryMode(false);
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const files = Array.from(e.target.files);
            const newAttachments: Attachment[] = files.map((file: File) => ({
                id: Math.random().toString(36).substr(2, 9) + Date.now().toString(),
                name: file.name,
                type: file.type.includes('image') ? 'image' : 'file',
                size: `${(file.size / 1024 / 1024).toFixed(2)} MB`
            }));
            setPendingAttachments(prev => [...prev, ...newAttachments]);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const isComplete = milestone.status === MilestoneStatus.APPROVED;
    const isInReview = milestone.status === MilestoneStatus.REVIEW;
    const isDraft = milestone.status === MilestoneStatus.DRAFT;

    return (
        <div className={`relative pl-8 md:pl-12 pb-10 group last:pb-0 ${isDraft ? 'opacity-80' : ''}`}>
            {/* Timeline Line - Stronger */}
            <div className={`absolute left-[7px] top-6 bottom-0 w-[2px] bg-zinc-800 group-last:bg-gradient-to-b group-last:from-zinc-800 group-last:to-transparent ${isDraft ? 'border-l border-dashed border-zinc-700 bg-transparent w-[1px] left-[7.5px]' : ''}`} />

            {/* Timeline Bullet - Console Style */}
            <div className={`absolute left-0 top-2 w-[16px] h-[16px] rounded-sm border-[2px] z-10 flex items-center justify-center transition-all shadow-sm ${isDraft ? 'border-zinc-700 bg-transparent' : 'border-black'
                } ${milestone.status === MilestoneStatus.IN_PROGRESS ? 'bg-emerald-500' : // Active Green
                    milestone.status === MilestoneStatus.REVIEW ? 'bg-amber-500' : // Review Amber
                        isComplete ? 'bg-zinc-600' :
                            isDraft ? '' : 'bg-zinc-800' // Complete/Pending/Draft
                }`}>
                {isComplete && <Check size={10} className="text-black" strokeWidth={3} />}
                {isInReview && <div className="w-1.5 h-1.5 rounded-full bg-black" />}
            </div>

            {/* Header Row */}
            <div className="flex flex-col md:flex-row md:items-start justify-between mb-4 gap-2">
                <div className="flex flex-col">
                    <div className="flex items-center gap-3">
                        <h3 className={`text-[15px] font-medium tracking-tight transition-colors ${isComplete ? 'text-zinc-500' : 'text-zinc-200'}`}>
                            {milestone.title}
                        </h3>
                        {/* Inline Status for quick scanning */}
                        <StatusBadge status={milestone.status} />
                    </div>

                    <div className="flex flex-wrap items-center gap-3 text-[11px] text-zinc-500 mt-1.5">
                        {expectedDate && !isDraft && (
                            <span className="flex items-center gap-1.5 font-mono">
                                <Clock size={11} />
                                {isComplete
                                    ? <span className="text-emerald-500">Completed {new Date(expectedDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                                    : <span>ETA: {new Date(expectedDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                                }
                            </span>
                        )}
                        {/* Assignee */}
                        {visibleAssignee ? (
                            <div
                                className={`flex items-center gap-1.5 px-2 py-0.5 rounded bg-zinc-900 border border-zinc-800 ${isProvider ? 'cursor-pointer hover:border-zinc-700' : ''} transition-colors`}
                                onClick={() => isProvider && setShowAssigneeSelector(!showAssigneeSelector)}
                            >
                                <div className={`w-3.5 h-3.5 rounded ${visibleAssignee.avatarColor} flex items-center justify-center text-[7px] text-white`}>{visibleAssignee.initials}</div>
                                {isProvider && <span className="text-[10px] text-zinc-500">{visibleAssignee.name.split(' ')[0]}</span>}
                            </div>
                        ) : (
                            isProvider && !isDraft && (
                                <button onClick={() => setShowAssigneeSelector(!showAssigneeSelector)} className="text-[10px] text-zinc-500 hover:text-zinc-300 flex items-center gap-1 hover:bg-zinc-900 px-1.5 py-0.5 rounded transition-colors">
                                    <Plus size={10} strokeWidth={1.5} /> Assign
                                </button>
                            )
                        )}
                        <div className="relative">
                            {showAssigneeSelector && (
                                <AssigneeSelector
                                    members={teamMembers}
                                    currentAssigneeId={milestone.assigneeId}
                                    onSelect={onAssign}
                                    onClose={() => setShowAssigneeSelector(false)}
                                    onInvite={onInvite}
                                />
                            )}
                        </div>
                    </div>
                </div>

                {/* Receiver Actions */}
                <div className="flex items-center gap-2">
                    {isReceiver && isInReview && (
                        <div className="flex gap-2 md:ml-2 animate-fade-in mt-2 md:mt-0">
                            <Button size="sm" onClick={onApprove} className="h-7 text-[11px] bg-emerald-500 hover:bg-emerald-600 text-black border-transparent font-semibold shadow-[0_0_10px_rgba(16,185,129,0.3)]">
                                Approve
                            </Button>
                            <Button size="sm" variant="secondary" className="h-7 text-[11px]">
                                Changes
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            {/* Content Area */}
            <div className="pl-0">
                {milestone.comments.length > 0 && (
                    <div className="mb-4 space-y-1">
                        {milestone.comments.map(c => (
                            <CommentItem
                                key={c.id}
                                comment={c}
                                userRole={role}
                                onEdit={(id, txt) => onEditComment(id, txt)}
                                onDelete={(id) => onDeleteComment(id)}
                            />
                        ))}
                    </div>
                )}

                {/* Composer */}
                {(!isComplete || milestone.comments.length > 0) && !isDraft && (
                    <div className={`rounded-lg border transition-all duration-300 overflow-hidden ${isDeliveryMode
                        ? 'bg-zinc-950 border-blue-900/50 shadow-[0_0_15px_rgba(30,58,138,0.2)]'
                        : 'bg-zinc-950 border-zinc-800'
                        }`}>
                        <div className="p-3">
                            <div className="flex gap-3">
                                <div className="w-6 h-6 rounded bg-zinc-900 flex items-center justify-center text-zinc-500 text-[9px] mt-1 shrink-0 border border-zinc-800">
                                    {role === UserRole.PROVIDER ? 'AD' : 'TS'}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <textarea
                                        className="w-full bg-transparent text-[13px] text-zinc-200 placeholder-zinc-600 resize-none focus:outline-none min-h-[32px] py-1 leading-relaxed"
                                        placeholder={isDeliveryMode ? "Describe this delivery for the receiver..." : "Write a comment..."}
                                        value={commentText}
                                        onChange={(e) => setCommentText(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                handleSubmitComment();
                                            }
                                        }}
                                    />

                                    {/* Attachments Preview */}
                                    {pendingAttachments.length > 0 && (
                                        <div className="flex flex-wrap gap-2 mt-3 mb-1">
                                            {pendingAttachments.map((att, i) => (
                                                <div key={i} className="flex items-center gap-2 bg-zinc-900 px-2 py-1.5 rounded text-[11px] text-zinc-300 border border-zinc-800 max-w-full">
                                                    <Paperclip size={10} className="text-zinc-500 shrink-0" />
                                                    <span className="truncate max-w-[150px]">{att.name}</span>
                                                    <button
                                                        onClick={() => setPendingAttachments(prev => prev.filter((_, idx) => idx !== i))}
                                                        className="text-zinc-500 hover:text-rose-400 ml-1 shrink-0"
                                                    >
                                                        <X size={12} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Action Bar */}
                                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-zinc-900">
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="file"
                                                multiple
                                                ref={fileInputRef}
                                                className="hidden"
                                                onChange={handleFileSelect}
                                            />
                                            <button
                                                className="text-zinc-500 hover:text-zinc-200 transition-colors p-1 rounded hover:bg-zinc-900 flex items-center gap-1.5"
                                                onClick={() => fileInputRef.current?.click()}
                                                title="Attach File"
                                            >
                                                <Paperclip size={14} strokeWidth={1.5} />
                                                <span className="text-[10px] font-medium hidden sm:inline">Attach</span>
                                            </button>

                                            {isProvider && (
                                                <button
                                                    onClick={() => setIsDeliveryMode(!isDeliveryMode)}
                                                    className={`flex items-center gap-1.5 text-[10px] font-medium px-2 py-1 rounded transition-all ${isDeliveryMode
                                                        ? 'text-blue-400 bg-blue-950/30 border border-blue-900/50'
                                                        : 'text-zinc-500 hover:text-zinc-200 hover:bg-zinc-900 border border-transparent'
                                                        }`}
                                                >
                                                    <CheckCircle2 size={12} strokeWidth={isDeliveryMode ? 2 : 1.5} />
                                                    {isDeliveryMode ? 'Delivery Mode' : 'Mark as Delivery'}
                                                </button>
                                            )}
                                        </div>

                                        <Button
                                            size="sm"
                                            onClick={handleSubmitComment}
                                            disabled={!commentText.trim() && pendingAttachments.length === 0}
                                            className={`h-6 text-[10px] px-3 transition-all ${isDeliveryMode ? 'bg-blue-600 hover:bg-blue-500 text-white border-transparent font-semibold shadow-[0_0_10px_rgba(37,99,235,0.3)]' : ''}`}
                                        >
                                            {isDeliveryMode ? (
                                                <span className="flex items-center gap-2">Deliver <ArrowLeft size={10} className="rotate-180" /></span>
                                            ) : 'Post'}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

// ... FilesTab and NotesTab components (no major responsive changes needed internally as grid/flex handles it, but check grid cols) ...
// Update FilesTab grid for mobile
const FilesTab: React.FC<{ project: Project; onAddFile?: (file: Attachment) => void }> = ({ project, onAddFile }) => {
    // ... logic same ...
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [filter, setFilter] = useState<'all' | 'image' | 'file'>('all');

    // Aggregate files from project.files and milestone comments
    const allFiles = useMemo(() => {
        const commentFiles = project.milestones.flatMap(m =>
            m.comments.flatMap(c => c.attachments || [])
        );
        return [...(project.files || []), ...commentFiles].sort((a, b) => {
            const dateA = a.uploadedAt || '';
            const dateB = b.uploadedAt || '';
            return dateB.localeCompare(dateA); // Newest first
        });
    }, [project]);

    const filteredFiles = allFiles.filter(f => filter === 'all' || f.type === filter);

    const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0 && onAddFile) {
            const file = e.target.files[0];
            const newAtt: Attachment = {
                id: Math.random().toString(36).substr(2, 9),
                name: file.name,
                type: file.type.includes('image') ? 'image' : 'file',
                size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
            };
            onAddFile(newAtt);
        }
    };

    return (
        <div className="animate-fade-in max-w-4xl">
            {/* Header / Filter */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
                <div className="flex items-center gap-2 bg-zinc-950 p-1 rounded-lg border border-zinc-800 self-start">
                    {(['all', 'image', 'file'] as const).map(f => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            className={`px-3 py-1.5 text-[12px] font-medium rounded-md transition-all ${filter === f ? 'bg-zinc-800 text-zinc-100 shadow-sm' : 'text-zinc-500 hover:text-zinc-300'
                                }`}
                        >
                            {f === 'all' ? 'All Files' : f === 'image' ? 'Images' : 'Documents'}
                        </button>
                    ))}
                </div>
                <div className="flex gap-2 self-start">
                    <input type="file" ref={fileInputRef} className="hidden" onChange={handleUpload} />
                    <Button size="sm" variant="secondary" onClick={() => fileInputRef.current?.click()}>
                        <Upload size={14} className="mr-2" /> Upload
                    </Button>
                </div>
            </div>

            {/* Empty State */}
            {filteredFiles.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-zinc-600 border border-zinc-800 border-dashed rounded-lg bg-zinc-950">
                    <div className="w-12 h-12 bg-zinc-900 rounded-full flex items-center justify-center mb-4 border border-zinc-800">
                        <FileText size={20} strokeWidth={1.5} className="text-zinc-500" />
                    </div>
                    <p className="text-[13px] font-medium text-zinc-500">No files found.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredFiles.map(file => (
                        <div key={file.id} className="group bg-zinc-950 border border-zinc-800 p-4 rounded-lg hover:border-zinc-700 transition-all flex flex-col justify-between h-[140px] shadow-sm hover:shadow-md">
                            <div className="flex items-start justify-between mb-2">
                                <div className="w-10 h-10 rounded bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-500">
                                    {getFileIcon(file.type)}
                                </div>
                                <button className="p-1.5 text-zinc-500 hover:text-zinc-200 hover:bg-zinc-900 rounded transition-colors opacity-0 group-hover:opacity-100">
                                    <Download size={14} />
                                </button>
                            </div>
                            <div>
                                <h4 className="text-[13px] font-medium text-zinc-300 truncate mb-1" title={file.name}>{file.name}</h4>
                                <div className="flex items-center gap-2 text-[11px] text-zinc-600">
                                    <span>{file.size}</span>
                                    <span>•</span>
                                    <span>{file.uploadedAt ? new Date(file.uploadedAt).toLocaleDateString() : 'Unknown date'}</span>
                                </div>
                                {file.uploadedBy && (
                                    <div className="mt-2 text-[10px] text-zinc-700 flex items-center gap-1">
                                        by <span className="text-zinc-600">{file.uploadedBy}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// ... NotesTab ...
const NotesTab: React.FC<{
    project: Project;
    onAdd: (title: string, content: string) => void;
    onUpdate: (id: string, title: string, content: string) => void;
    onDelete: (id: string) => void;
}> = ({ project, onAdd, onUpdate, onDelete }) => {
    // ... existing state ...
    const [view, setView] = useState<'list' | 'editor'>('list');
    const [activeNoteId, setActiveNoteId] = useState<string | null>(null);
    const [editorTitle, setEditorTitle] = useState('');
    const [editorContent, setEditorContent] = useState('');

    const handleCreate = () => {
        setEditorTitle('');
        setEditorContent('');
        setActiveNoteId(null);
        setView('editor');
    };

    const handleEdit = (note: Note) => {
        setEditorTitle(note.title);
        setEditorContent(note.content);
        setActiveNoteId(note.id);
        setView('editor');
    };

    const handleSave = () => {
        if (!editorTitle.trim()) return;
        if (activeNoteId) {
            onUpdate(activeNoteId, editorTitle, editorContent);
        } else {
            onAdd(editorTitle, editorContent);
        }
        setView('list');
    };

    if (view === 'editor') {
        return (
            <div className="animate-fade-in max-w-3xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                    <button onClick={() => setView('list')} className="text-[13px] text-zinc-500 hover:text-zinc-200 flex items-center gap-2 transition-colors">
                        <ArrowLeft size={14} /> Back to Notes
                    </button>
                    <Button size="sm" onClick={handleSave}>
                        <Save size={14} className="mr-2" /> Save Note
                    </Button>
                </div>
                <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-6 md:p-8 min-h-[60vh] shadow-sm">
                    <input
                        className="w-full bg-transparent text-xl md:text-2xl font-medium text-zinc-100 placeholder-zinc-600 focus:outline-none mb-6"
                        placeholder="Untitled Note"
                        value={editorTitle}
                        onChange={(e) => setEditorTitle(e.target.value)}
                        autoFocus
                    />
                    <textarea
                        className="w-full bg-transparent text-[14px] text-zinc-300 placeholder-zinc-700 focus:outline-none leading-relaxed resize-none min-h-[400px]"
                        placeholder="Start typing..."
                        value={editorContent}
                        onChange={(e) => setEditorContent(e.target.value)}
                    />
                </div>
            </div>
        )
    }

    return (
        <div className="animate-fade-in max-w-4xl">
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-[14px] font-medium text-zinc-200">Project Docs</h3>
                <Button size="sm" variant="secondary" onClick={handleCreate}>
                    <Plus size={14} className="mr-2" /> New Note
                </Button>
            </div>

            {(project.notes || []).length === 0 ? (
                <div onClick={handleCreate} className="group flex flex-col items-center justify-center py-20 text-zinc-600 border border-zinc-800 border-dashed rounded-lg bg-zinc-950 cursor-pointer hover:bg-zinc-900 transition-colors">
                    <div className="w-12 h-12 bg-zinc-900 rounded-full flex items-center justify-center mb-4 border border-zinc-800 group-hover:border-zinc-700">
                        <StickyNote size={20} strokeWidth={1.5} className="text-zinc-600 group-hover:text-zinc-400" />
                    </div>
                    <p className="text-[13px] font-medium text-zinc-500 group-hover:text-zinc-300">Create your first note</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(project.notes || []).map(note => (
                        <div key={note.id} onClick={() => handleEdit(note)} className="group bg-zinc-950 border border-zinc-800 p-5 rounded-lg hover:border-zinc-700 cursor-pointer transition-all shadow-sm hover:shadow-md">
                            <div className="flex justify-between items-start mb-3">
                                <div className="w-8 h-8 rounded bg-zinc-900 flex items-center justify-center text-zinc-500">
                                    <FileText size={14} />
                                </div>
                                <button
                                    onClick={(e) => {
                                        // #region agent log
                                        fetch('http://127.0.0.1:7243/ingest/02fb56fd-40d8-458d-939a-d486f51f7d8b', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ location: 'ProjectView.tsx:817', message: 'Delete button clicked', data: { noteId: note.id, hasOnDelete: !!onDelete }, timestamp: Date.now(), sessionId: 'debug-session', runId: 'run1', hypothesisId: 'H2' }) }).catch(() => { });
                                        // #endregion
                                        e.stopPropagation();
                                        onDelete(note.id);
                                        // #region agent log
                                        fetch('http://127.0.0.1:7243/ingest/02fb56fd-40d8-458d-939a-d486f51f7d8b', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ location: 'ProjectView.tsx:821', message: 'onDelete called', data: { noteId: note.id }, timestamp: Date.now(), sessionId: 'debug-session', runId: 'run1', hypothesisId: 'H2' }) }).catch(() => { });
                                        // #endregion
                                    }}
                                    className="text-zinc-500 hover:text-rose-400 opacity-0 group-hover:opacity-100 transition-opacity p-1"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                            <h4 className="text-[14px] font-medium text-zinc-200 mb-2 truncate">{note.title}</h4>
                            <p className="text-[13px] text-zinc-500 line-clamp-3 mb-4 leading-relaxed h-[60px]">
                                {note.content || "No content"}
                            </p>
                            <div className="flex items-center justify-between pt-3 border-t border-zinc-900">
                                <span className="text-[11px] text-zinc-600">
                                    Updated {new Date(note.updatedAt).toLocaleDateString()}
                                </span>
                                <div className="flex items-center gap-1.5">
                                    <div className="w-4 h-4 rounded-full bg-zinc-800 flex items-center justify-center text-[8px] text-zinc-400">
                                        {note.author[0]}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};


export const ProjectView: React.FC<ProjectViewProps> = ({
    project,
    role,
    teamMembers,
    onBack,
    onUpdateMilestone,
    onAssignMilestone,
    onInviteMember,
    onUpdateProjectStatus,
    onUpdateAssigneeVisibility,
    onUpdateProjectQuestions,
    onUpdateMilestoneTitle,
    onAddComment,
    onResolveComment,
    onEditComment,
    onDeleteComment,
    onDeleteProject,
    onArchiveProject,
    onRestoreProject,
    onOpenReceiverView,
    onUpdateMilestoneDuration,
    onDeliverWork,
    onApproveMilestone,
    // Notes & Files
    onAddNote,
    onUpdateNote,
    onDeleteNote,
    onAddProjectFile,
    onFinalizeProject,
    onUpdateProject,
    onCompleteDraft
}) => {
    const isProvider = role === UserRole.PROVIDER;
    const isReceiver = role === UserRole.RECEIVER;
    const isArchived = project.status === ProjectStatus.ARCHIVED;
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [isStatusMenuOpen, setIsStatusMenuOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');

    // Menu State
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
    const menuButtonRef = useRef<HTMLButtonElement>(null);

    // Edit/Details Modals
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

    // Edit Form State
    const [editName, setEditName] = useState(project.name);
    const [editDescription, setEditDescription] = useState(project.description || '');

    // Quick Note State
    const [quickNote, setQuickNote] = useState('');

    // Resend invite state
    const [isResendingInvite, setIsResendingInvite] = useState(false);
    const [resendInviteSuccess, setResendInviteSuccess] = useState(false);
    const [resendInviteError, setResendInviteError] = useState<string | null>(null);
    const [lastInviteSentAt, setLastInviteSentAt] = useState<number | null>(null);
    const [dailyResendCount, setDailyResendCount] = useState<number>(0);
    const [dailyResendResetTime, setDailyResendResetTime] = useState<number>(Date.now() + 24 * 60 * 60 * 1000);

    // Edit onboarding modal state (full 4-step modal matching Dashboard)
    const [isEditOnboardingOpen, setIsEditOnboardingOpen] = useState(false);
    const [editOnboardingStep, setEditOnboardingStep] = useState<1 | 2 | 3 | 4>(1);
    const [editFormData, setEditFormData] = useState({
        name: project.name,
        receiverName: project.receiverName,
        receiverEmail: project.receiverEmail,
        description: project.description || '',
        startDate: project.startDate ? new Date(project.startDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
    });
    const [editQuestions, setEditQuestions] = useState<OnboardingQuestion[]>(
        project.onboardingQuestions || [{ id: Math.random().toString(), text: '', required: true }]
    );
    const [editMilestones, setEditMilestones] = useState<Milestone[]>(project.milestones || []);
    const [editIncludeOnboarding, setEditIncludeOnboarding] = useState(true);
    const [editIsGenerating, setEditIsGenerating] = useState(false);
    const [editFormError, setEditFormError] = useState<string | null>(null);
    const [editGeneratedLink, setEditGeneratedLink] = useState('');
    const [editEmailMessage, setEditEmailMessage] = useState('');
    const [editIsEditingEmail, setEditIsEditingEmail] = useState(false);

    useEffect(() => {
        setEditName(project.name);
        setEditDescription(project.description || '');
    }, [project]);

    useEffect(() => {
        if (isMenuOpen) {
            const handleScroll = () => setIsMenuOpen(false);
            window.addEventListener('scroll', handleScroll, true);
            window.addEventListener('resize', handleScroll);
            return () => {
                window.removeEventListener('scroll', handleScroll, true);
                window.removeEventListener('resize', handleScroll);
            };
        }
    }, [isMenuOpen]);

    const milestoneDates = useMemo(() => {
        const dates: Record<string, string> = {};
        if (!project.milestones.length) return dates;
        let cursor = project.startDate ? new Date(project.startDate).getTime() : Date.now();
        project.milestones.forEach(m => {
            const isComplete = m.status === MilestoneStatus.APPROVED;
            if (isComplete && m.completedAt) {
                cursor = new Date(m.completedAt).getTime();
                dates[m.id] = new Date(cursor).toISOString();
            } else {
                if (project.status === ProjectStatus.ACTIVE) cursor = Math.max(cursor, Date.now());
                cursor += m.duration * 86400000;
                dates[m.id] = new Date(cursor).toISOString();
            }
        });
        return dates;
    }, [project]);

    // Aggregate files from project.files and milestone comments for sidebar widget
    const allFiles = useMemo(() => {
        const commentFiles = project.milestones.flatMap(m =>
            m.comments.flatMap(c => c.attachments || [])
        );
        return [...(project.files || []), ...commentFiles].sort((a, b) => {
            const dateA = a.uploadedAt || '';
            const dateB = b.uploadedAt || '';
            return dateB.localeCompare(dateA);
        }).slice(0, 5); // Take top 5 recent files
    }, [project]);

    const hasReceiverOnboarded = (project.onboardingAnswers || []).length > 0;
    const isDraft = project.status === ProjectStatus.DRAFT;
    const isWaitingForReceiver = project.status === ProjectStatus.WAITING_ON_RECEIVER;

    // Load resend invite state from localStorage on mount
    useEffect(() => {
        const stored = localStorage.getItem(`resend_invite_${project.id}`);
        if (stored) {
            try {
                const data = JSON.parse(stored);
                setLastInviteSentAt(data.lastInviteSentAt || null);
                setDailyResendCount(data.dailyResendCount || 0);
                setDailyResendResetTime(data.dailyResendResetTime || Date.now() + 24 * 60 * 60 * 1000);
            } catch (e) {
                // Ignore parse errors
            }
        }
    }, [project.id]);

    // Reset daily count if reset time has passed
    useEffect(() => {
        if (dailyResendResetTime <= Date.now()) {
            setDailyResendCount(0);
            setDailyResendResetTime(Date.now() + 24 * 60 * 60 * 1000);
            const stored = localStorage.getItem(`resend_invite_${project.id}`);
            if (stored) {
                try {
                    const data = JSON.parse(stored);
                    localStorage.setItem(`resend_invite_${project.id}`, JSON.stringify({
                        ...data,
                        dailyResendCount: 0,
                        dailyResendResetTime: Date.now() + 24 * 60 * 60 * 1000
                    }));
                } catch (e) { }
            }
        }
    }, [dailyResendResetTime, project.id]);

    // Calculate cooldown time remaining
    const cooldownRemaining = useMemo(() => {
        if (!lastInviteSentAt) return 0;
        const elapsed = Date.now() - lastInviteSentAt;
        const cooldownMs = 5 * 60 * 1000; // 5 minutes
        return Math.max(0, cooldownMs - elapsed);
    }, [lastInviteSentAt]);

    const canResendInvite = useMemo(() => {
        if (hasReceiverOnboarded) return false;
        if (dailyResendCount >= 5) return false;
        if (cooldownRemaining > 0) return false;
        return true;
    }, [hasReceiverOnboarded, dailyResendCount, cooldownRemaining]);

    const formatCooldown = (ms: number) => {
        const minutes = Math.floor(ms / 60000);
        const seconds = Math.floor((ms % 60000) / 1000);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    // Update edit form data when project changes
    useEffect(() => {
        setEditFormData({
            name: project.name,
            receiverName: project.receiverName,
            receiverEmail: project.receiverEmail,
            description: project.description || '',
            startDate: project.startDate ? new Date(project.startDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
        });
        setEditQuestions(project.onboardingQuestions || [{ id: Math.random().toString(), text: '', required: true }]);
        setEditMilestones(project.milestones || []);
        // Generate link if not set
        if (!editGeneratedLink) {
            setEditGeneratedLink(`${window.location.origin}/onboard/${project.id}`);
        }
        // Set email message from description (always sync with description when modal opens)
        setEditEmailMessage(project.description || '');
    }, [project]);

    const handleResendInvite = async () => {
        if (!canResendInvite || hasReceiverOnboarded) return;

        setIsResendingInvite(true);
        setResendInviteError(null);
        setResendInviteSuccess(false);

        try {
            await sendProjectInviteEmail({
                to: project.receiverEmail,
                projectName: project.name,
                projectDescription: project.description || '',
            });

            // Update resend state
            const now = Date.now();
            setLastInviteSentAt(now);
            const newCount = dailyResendCount + 1;
            setDailyResendCount(newCount);

            // Save to localStorage
            localStorage.setItem(`resend_invite_${project.id}`, JSON.stringify({
                lastInviteSentAt: now,
                dailyResendCount: newCount,
                dailyResendResetTime: dailyResendResetTime
            }));

            setResendInviteSuccess(true);
            setTimeout(() => setResendInviteSuccess(false), 3000);
        } catch (error) {
            console.error('Error resending invitation email:', error);
            setResendInviteError('Failed to send invitation email. Please try again.');
            setTimeout(() => setResendInviteError(null), 5000);
        } finally {
            setIsResendingInvite(false);
        }
    };

    const handleOpenEditOnboarding = () => {
        setEditFormData({
            name: project.name,
            receiverName: project.receiverName,
            receiverEmail: project.receiverEmail,
            description: project.description || '',
            startDate: project.startDate ? new Date(project.startDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
        });
        setEditQuestions(project.onboardingQuestions || [{ id: Math.random().toString(), text: '', required: true }]);
        setEditMilestones(project.milestones || []);
        setEditGeneratedLink(`${window.location.origin}/onboard/${project.id}`);
        setEditEmailMessage(project.description || '');
        setEditOnboardingStep(1);
        setEditFormError(null);
        setIsEditOnboardingOpen(true);
    };

    const handleEditOnboardingNext = (skip?: boolean) => {
        if (editOnboardingStep === 1) {
            if (!editFormData.name.trim() || !editFormData.receiverName.trim() || !/\S+@\S+\.\S+/.test(editFormData.receiverEmail.trim())) {
                setEditFormError('Please fill in all required fields.');
                return;
            }
            setEditOnboardingStep(2);
        } else if (editOnboardingStep === 2) {
            setEditOnboardingStep(3);
        } else if (editOnboardingStep === 3) {
            setEditOnboardingStep(4);
        }
        setEditFormError(null);
    };

    const handleEditOnboardingResend = async () => {
        if (!canResendInvite || hasReceiverOnboarded) return;

        // Update project data first
        if (onUpdateProject) {
            onUpdateProject(project.id, {
                name: editFormData.name.trim(),
                receiverName: editFormData.receiverName.trim(),
                receiverEmail: editFormData.receiverEmail.trim(),
                description: editFormData.description.trim(),
                startDate: editFormData.startDate,
                milestones: editMilestones,
            });
        }

        // Update onboarding questions
        if (onUpdateProjectQuestions) {
            const validQuestions = editQuestions.filter(q => q.text.trim().length > 0);
            onUpdateProjectQuestions(project.id, validQuestions);
        }

        setIsEditOnboardingOpen(false);
        setIsResendingInvite(true);
        setResendInviteError(null);
        setResendInviteSuccess(false);

        try {
            await sendProjectInviteEmail({
                to: editFormData.receiverEmail.trim(),
                projectName: editFormData.name.trim(),
                projectDescription: editEmailMessage.trim() || editFormData.description.trim(),
            });

            // Update resend state
            const now = Date.now();
            setLastInviteSentAt(now);
            const newCount = dailyResendCount + 1;
            setDailyResendCount(newCount);

            // Save to localStorage
            localStorage.setItem(`resend_invite_${project.id}`, JSON.stringify({
                lastInviteSentAt: now,
                dailyResendCount: newCount,
                dailyResendResetTime: dailyResendResetTime
            }));

            setResendInviteSuccess(true);
            setTimeout(() => setResendInviteSuccess(false), 3000);
        } catch (error) {
            console.error('Error sending invitation email:', error);
            setResendInviteError('Failed to send invitation email. Please try again.');
            setTimeout(() => setResendInviteError(null), 5000);
        } finally {
            setIsResendingInvite(false);
        }
    };

    const handleSaveQuickNote = () => {
        if (quickNote.trim() && onAddNote) {
            onAddNote(project.id, `Quick Note - ${new Date().toLocaleDateString()}`, quickNote);
            setQuickNote('');
            setActiveTab('notes'); // Optional: switch to notes tab to show it was saved
        }
    };

    const handleMenuClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!isMenuOpen && menuButtonRef.current) {
            const rect = menuButtonRef.current.getBoundingClientRect();
            setMenuPos({
                top: rect.bottom + 8,
                left: rect.right - 200 // Align roughly to right edge
            });
        }
        setIsMenuOpen(!isMenuOpen);
    };

    const handleSaveEdit = () => {
        if (onUpdateProject) {
            onUpdateProject(project.id, { name: editName, description: editDescription });
            setIsEditModalOpen(false);
        }
    };

    const handleMenuAction = (action: string) => {
        setIsMenuOpen(false);
        if (action === 'edit') setIsEditModalOpen(true);
        if (action === 'access') setIsShareModalOpen(true);
        if (action === 'archive') onArchiveProject(project.id);
        if (action === 'restore') onRestoreProject(project.id);
        if (action === 'delete') {
            if (confirm('Are you sure you want to permanently delete this project? This action cannot be undone.')) {
                onDeleteProject(project.id);
            }
        }
        if (action === 'details' || action === 'view_onboarding') setIsDetailsModalOpen(true);
        if (action === 'download') alert('Downloading project summary...');
        if (action === 'pause') alert('Project paused (placeholder)');
    };

    return (
        <div className="flex h-full bg-black animate-fade-in relative">
            <div className="flex-1 flex flex-col min-w-0 overflow-y-auto">

                {/* Header */}
                <div className="px-4 md:px-10 pt-6 md:pt-8 pb-4 shrink-0 bg-black z-10">
                    <div className="flex items-center gap-3 mb-6">
                        <button onClick={onBack} className="flex items-center gap-1.5 text-zinc-500 hover:text-zinc-200 transition-colors text-[13px] group">
                            <ArrowLeft size={14} strokeWidth={1.5} className="group-hover:-translate-x-0.5 transition-transform" /> Back
                        </button>
                        <div className="flex items-center gap-2 text-[12px] text-zinc-700">
                            <span className="text-zinc-700">/</span>
                            <span>Projects</span>
                        </div>
                    </div>

                    <div className="flex flex-col md:flex-row md:items-start justify-between mb-8 gap-4">
                        <div>
                            <h1 className="text-2xl md:text-3xl font-medium text-zinc-100 leading-tight mb-2 tracking-tight">{project.name}</h1>
                            <p className="text-zinc-500 text-[13px] max-w-2xl font-normal">{project.description || "No description provided."}</p>
                        </div>

                        <div className="flex gap-2 self-start md:self-auto relative">
                            {isProvider && isDraft && onCompleteDraft && (
                                <Button variant="primary" onClick={() => onCompleteDraft(project.id)} className="h-8 text-[11px]">
                                    <Rocket size={12} strokeWidth={1.5} className="mr-2" /> Complete & Send
                                </Button>
                            )}
                            {isProvider && !isDraft && (
                                <Button variant="secondary" onClick={() => setIsShareModalOpen(true)} className="h-8 text-[11px]">
                                    <Share2 size={12} strokeWidth={1.5} className="mr-2" /> Share
                                </Button>
                            )}
                            <button
                                ref={menuButtonRef}
                                onClick={handleMenuClick}
                                className={`w-8 h-8 rounded bg-transparent border border-zinc-800 hover:bg-zinc-900 flex items-center justify-center text-zinc-500 hover:text-zinc-200 transition-colors ${isMenuOpen ? 'bg-zinc-900 text-zinc-200' : ''}`}
                            >
                                <MoreVertical size={16} strokeWidth={1.5} />
                            </button>

                            {isMenuOpen && createPortal(
                                <div className="fixed inset-0 z-50 flex flex-col" style={{ pointerEvents: 'auto' }}>
                                    {/* Backdrop */}
                                    <div className="fixed inset-0 bg-transparent" onClick={(e) => { e.stopPropagation(); setIsMenuOpen(false); }} />

                                    {/* Menu */}
                                    <div
                                        className="fixed z-50 w-48 bg-zinc-950 border border-zinc-800 rounded-lg shadow-xl py-1 overflow-hidden animate-fade-in"
                                        style={{ top: menuPos.top, left: menuPos.left }}
                                    >
                                        {isProvider ? (
                                            <>
                                                <button onClick={() => handleMenuAction('edit')} className="w-full text-left px-3 py-2 text-[12px] text-zinc-300 hover:bg-zinc-900 flex items-center gap-2">
                                                    <Settings size={12} /> Edit project details
                                                </button>
                                                <button onClick={() => handleMenuAction('access')} className="w-full text-left px-3 py-2 text-[12px] text-zinc-300 hover:bg-zinc-900 flex items-center gap-2">
                                                    <Users size={12} /> Manage access
                                                </button>
                                                {(project.onboardingAnswers || []).length > 0 && (
                                                    <button onClick={() => handleMenuAction('view_onboarding')} className="w-full text-left px-3 py-2 text-[12px] text-zinc-300 hover:bg-zinc-900 flex items-center gap-2">
                                                        <FileText size={12} /> View onboarding answers
                                                    </button>
                                                )}
                                                <button onClick={() => handleMenuAction('pause')} className="w-full text-left px-3 py-2 text-[12px] text-zinc-300 hover:bg-zinc-900 flex items-center gap-2 opacity-50 cursor-not-allowed" title="Not available">
                                                    <PauseCircle size={12} /> Pause project
                                                </button>
                                                <div className="h-px bg-zinc-900 my-1"></div>
                                                {!isArchived && !isDraft && (
                                                    <button onClick={() => handleMenuAction('archive')} className="w-full text-left px-3 py-2 text-[12px] text-zinc-300 hover:bg-zinc-900 flex items-center gap-2">
                                                        <Archive size={12} /> Archive project
                                                    </button>
                                                )}
                                                {isArchived && (
                                                    <>
                                                        <button onClick={() => handleMenuAction('restore')} className="w-full text-left px-3 py-2 text-[12px] text-zinc-300 hover:bg-zinc-900 flex items-center gap-2">
                                                            <Archive size={12} /> Restore project
                                                        </button>
                                                        <button onClick={() => handleMenuAction('delete')} className="w-full text-left px-3 py-2 text-[12px] text-red-400 hover:bg-red-900/10 flex items-center gap-2">
                                                            <Trash2 size={12} /> Delete project
                                                        </button>
                                                    </>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                <button onClick={() => handleMenuAction('details')} className="w-full text-left px-3 py-2 text-[12px] text-zinc-300 hover:bg-zinc-900 flex items-center gap-2">
                                                    <Info size={12} /> View project details
                                                </button>
                                                <button onClick={() => handleMenuAction('download')} className="w-full text-left px-3 py-2 text-[12px] text-zinc-300 hover:bg-zinc-900 flex items-center gap-2">
                                                    <Download size={12} /> Download summary
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>,
                                document.body
                            )}
                        </div>
                    </div>

                    {/* Properties Grid */}
                    <div className="flex flex-col md:flex-row gap-6 md:gap-12 mb-8 text-[13px] border-b border-zinc-900 pb-6">
                        <div className="space-y-1">
                            <div className="text-zinc-500 flex items-center gap-2 font-medium uppercase text-[10px] tracking-wider mb-1">Status</div>
                            <div className="relative">
                                <button
                                    onClick={() => isProvider && setIsStatusMenuOpen(!isStatusMenuOpen)}
                                    className={`transition-opacity ${isProvider ? 'hover:opacity-80' : 'cursor-default'}`}
                                >
                                    <StatusBadge status={project.status} />
                                </button>
                                {isStatusMenuOpen && isProvider && (
                                    <div className="absolute top-full left-0 mt-2 w-48 bg-zinc-950 border border-zinc-800 rounded-lg shadow-xl py-1 z-50">
                                        {[ProjectStatus.ACTIVE, ProjectStatus.WAITING_ON_RECEIVER, ProjectStatus.COMPLETED].map(s => (
                                            <button
                                                key={s}
                                                onClick={() => { onUpdateProjectStatus(project.id, s); setIsStatusMenuOpen(false); }}
                                                className="w-full text-left px-3 py-1.5 hover:bg-zinc-900 transition-colors"
                                            >
                                                <StatusBadge status={s} />
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="space-y-1">
                            <div className="text-zinc-500 flex items-center gap-2 font-medium uppercase text-[10px] tracking-wider mb-1">Receiver</div>
                            <div className="text-zinc-200 font-medium">{project.receiverName}</div>
                        </div>

                        <div className="space-y-1">
                            <div className="text-zinc-500 flex items-center gap-2 font-medium uppercase text-[10px] tracking-wider mb-1">Date</div>
                            <div className="text-zinc-500 font-mono text-[12px]">
                                {project.startDate ? new Date(project.startDate).toLocaleDateString() : 'Not started'}
                            </div>
                        </div>
                    </div>

                    {/* DRAFT BANNER */}
                    {isDraft && hasReceiverOnboarded && isProvider && (
                        <div className="mb-8 p-4 bg-emerald-950/20 border border-emerald-900/30 rounded-lg flex flex-col md:flex-row md:items-center justify-between animate-fade-in gap-4">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-emerald-500/10 flex items-center justify-center text-emerald-500 shrink-0">
                                    <CheckCircle2 size={16} />
                                </div>
                                <div>
                                    <h3 className="text-[13px] font-medium text-emerald-100">Receiver onboarding complete</h3>
                                    <p className="text-[12px] text-emerald-500/80">Review receiver answers and finalize timeline to begin work.</p>
                                </div>
                            </div>
                            <Button
                                className="bg-emerald-600 hover:bg-emerald-500 text-white border-transparent text-[12px] w-full md:w-auto"
                                onClick={onFinalizeProject}
                            >
                                Finalize Timeline & Start Project
                            </Button>
                        </div>
                    )}
                    {isWaitingForReceiver && !hasReceiverOnboarded && isProvider && (
                        <div className="mb-8 p-4 bg-zinc-900/40 border border-zinc-800 rounded-lg flex flex-col md:flex-row md:items-center justify-between gap-3 animate-fade-in">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-zinc-500 shrink-0">
                                    <Clock size={16} />
                                </div>
                                <div>
                                    <h3 className="text-[13px] font-medium text-zinc-300">Waiting for receiver onboarding</h3>
                                    <p className="text-[12px] text-zinc-500">Milestones are in draft mode until the receiver joins.</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                                {resendInviteSuccess && (
                                    <span className="text-[12px] text-emerald-400">Invite sent!</span>
                                )}
                                {resendInviteError && (
                                    <span className="text-[12px] text-red-400">{resendInviteError}</span>
                                )}
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={handleResendInvite}
                                    disabled={!canResendInvite || isResendingInvite}
                                    className="text-[12px] h-7"
                                    title={
                                        hasReceiverOnboarded
                                            ? 'Receiver has already accepted onboarding'
                                            : dailyResendCount >= 5
                                                ? 'Invite limit reached. Try again tomorrow.'
                                                : cooldownRemaining > 0
                                                    ? `Resend available in ${formatCooldown(cooldownRemaining)}`
                                                    : 'Resend invitation email'
                                    }
                                >
                                    {isResendingInvite
                                        ? 'Sending...'
                                        : dailyResendCount >= 5
                                            ? 'Limit reached'
                                            : cooldownRemaining > 0
                                                ? `Resend in ${formatCooldown(cooldownRemaining)}`
                                                : 'Resend invite'}
                                </Button>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={handleOpenEditOnboarding}
                                    className="text-[12px] h-7"
                                >
                                    Edit onboarding
                                </Button>
                            </div>
                        </div>
                    )}


                    {/* Tabs */}
                    <div className="flex gap-6 mb-6 overflow-x-auto no-scrollbar">
                        <button onClick={() => setActiveTab('overview')} className={`pb-1 text-[13px] font-medium transition-all border-b-2 whitespace-nowrap ${activeTab === 'overview' ? 'text-zinc-100 border-zinc-100' : 'text-zinc-500 border-transparent hover:text-zinc-300'}`}>Timeline</button>
                        <button onClick={() => setActiveTab('files')} className={`pb-1 text-[13px] font-medium transition-all border-b-2 whitespace-nowrap ${activeTab === 'files' ? 'text-zinc-100 border-zinc-100' : 'text-zinc-500 border-transparent hover:text-zinc-300'}`}>Files</button>
                        <button onClick={() => setActiveTab('notes')} className={`pb-1 text-[13px] font-medium transition-all border-b-2 whitespace-nowrap ${activeTab === 'notes' ? 'text-zinc-100 border-zinc-100' : 'text-zinc-500 border-transparent hover:text-zinc-300'}`}>Notes</button>
                    </div>
                </div>

                {/* 2. Timeline Area */}
                <div className="px-4 md:px-10 pb-20">
                    {activeTab === 'overview' && (
                        <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-10 items-start">
                            {/* Left: Timeline */}
                            <div className="min-w-0">
                                {project.milestones.map(m => (
                                    <MilestoneTimelineItem
                                        key={m.id}
                                        milestone={m}
                                        project={project}
                                        role={role}
                                        teamMembers={teamMembers}
                                        onStatusChange={(s) => onUpdateMilestone(project.id, m.id, s)}
                                        onAssign={(uid) => onAssignMilestone(project.id, m.id, uid)}
                                        onComment={(txt, att, del) => onAddComment(project.id, m.id, txt, att, del)}
                                        onEditComment={(cid, txt) => onEditComment && onEditComment(project.id, m.id, cid, txt)}
                                        onDeleteComment={(cid) => onDeleteComment && onDeleteComment(project.id, m.id, cid)}
                                        onDeliver={(txt, att) => onDeliverWork && onDeliverWork(project.id, m.id, txt, att)}
                                        onApprove={() => onApproveMilestone && onApproveMilestone(project.id, m.id)}
                                        expectedDate={milestoneDates[m.id]}
                                        onInvite={onInviteMember}
                                    />
                                ))}

                                {isProvider && (
                                    <button
                                        onClick={() => setIsShareModalOpen(true)}
                                        className="ml-8 mt-4 flex items-center gap-2 text-zinc-500 hover:text-zinc-200 px-2 py-1.5 rounded transition-all group"
                                    >
                                        <Plus size={14} strokeWidth={1.5} />
                                        <span className="text-[13px]">Add Milestone</span>
                                    </button>
                                )}
                            </div>

                            {/* Right: Sidebar */}
                            <div className="hidden xl:flex flex-col gap-6 sticky top-6">

                                {/* Scratchpad Widget */}
                                <div className="bg-zinc-900/30 border border-zinc-800 rounded-lg p-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <h3 className="text-[11px] font-medium text-zinc-500 uppercase tracking-wider flex items-center gap-2">
                                            <StickyNote size={12} /> Quick Note
                                        </h3>
                                    </div>
                                    <textarea
                                        className="w-full bg-transparent text-[13px] text-zinc-300 placeholder-zinc-700 resize-none focus:outline-none min-h-[100px] leading-relaxed"
                                        placeholder="Jot down quick thoughts or todos for this project..."
                                        value={quickNote}
                                        onChange={(e) => setQuickNote(e.target.value)}
                                    />
                                    <div className="flex justify-end mt-2 pt-2 border-t border-zinc-800/50">
                                        <button
                                            onClick={handleSaveQuickNote}
                                            disabled={!quickNote.trim()}
                                            className="text-[11px] font-medium text-emerald-500 hover:text-emerald-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                        >
                                            Save to Notes
                                        </button>
                                    </div>
                                </div>

                                {/* Recent Files Widget */}
                                <div className="bg-zinc-900/30 border border-zinc-800 rounded-lg p-4">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-[11px] font-medium text-zinc-500 uppercase tracking-wider flex items-center gap-2">
                                            <Paperclip size={12} /> Recent Files
                                        </h3>
                                        <button onClick={() => setActiveTab('files')} className="text-[10px] text-zinc-600 hover:text-zinc-400">View All</button>
                                    </div>

                                    {allFiles.length === 0 ? (
                                        <div className="text-center py-6 text-zinc-600 text-[12px] border border-dashed border-zinc-800/50 rounded">
                                            No files yet
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            {allFiles.map(file => (
                                                <div key={file.id} className="group flex items-center gap-3 p-2 rounded hover:bg-zinc-900 transition-colors cursor-pointer border border-transparent hover:border-zinc-800/50">
                                                    <div className="w-8 h-8 rounded bg-zinc-950 flex items-center justify-center text-zinc-500 border border-zinc-800 group-hover:text-zinc-300">
                                                        {getFileIcon(file.type)}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-[12px] text-zinc-300 truncate font-medium group-hover:text-zinc-100">{file.name}</div>
                                                        <div className="text-[10px] text-zinc-600">{file.size} • {new Date(file.uploadedAt || '').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</div>
                                                    </div>
                                                    <Download size={12} className="text-zinc-600 opacity-0 group-hover:opacity-100" />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                            </div>
                        </div>
                    )}
                    {activeTab === 'files' && (
                        <FilesTab project={project} onAddFile={(f) => onAddProjectFile && onAddProjectFile(project.id, f)} />
                    )}
                    {activeTab === 'notes' && (
                        <NotesTab
                            project={project}
                            onAdd={(title, content) => onAddNote && onAddNote(project.id, title, content)}
                            onUpdate={(id, title, content) => onUpdateNote && onUpdateNote(project.id, id, title, content)}
                            onDelete={(id) => {
                                // #region agent log
                                fetch('http://127.0.0.1:7243/ingest/02fb56fd-40d8-458d-939a-d486f51f7d8b', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ location: 'ProjectView.tsx:1554', message: 'NotesTab onDelete called', data: { projectId: project.id, noteId: id, hasOnDeleteNote: !!onDeleteNote }, timestamp: Date.now(), sessionId: 'debug-session', runId: 'run1', hypothesisId: 'H3' }) }).catch(() => { });
                                // #endregion
                                onDeleteNote && onDeleteNote(project.id, id);
                                // #region agent log
                                fetch('http://127.0.0.1:7243/ingest/02fb56fd-40d8-458d-939a-d486f51f7d8b', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ location: 'ProjectView.tsx:1558', message: 'onDeleteNote call completed', data: { projectId: project.id, noteId: id }, timestamp: Date.now(), sessionId: 'debug-session', runId: 'run1', hypothesisId: 'H3' }) }).catch(() => { });
                                // #endregion
                            }}
                        />
                    )}
                </div>
            </div>

            {/* Edit Onboarding Modal - Full 4-step structure matching New Project modal */}
            <Modal isOpen={isEditOnboardingOpen} onClose={() => setIsEditOnboardingOpen(false)} title="Edit Onboarding" maxWidth="max-w-4xl" noPadding>
                <div className="flex flex-col md:flex-row h-[600px] md:h-[560px] w-full bg-white dark:bg-zinc-950 relative">
                    <button
                        onClick={() => setIsEditOnboardingOpen(false)}
                        className="absolute top-4 right-4 z-20 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 transition-colors p-1 rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-900 md:block hidden"
                    >
                        <X size={18} />
                    </button>

                    <button
                        onClick={() => setIsEditOnboardingOpen(false)}
                        className="absolute top-2 right-2 z-20 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 transition-colors p-2 rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-900 md:hidden block"
                    >
                        <X size={18} />
                    </button>

                    <div className="w-full md:w-60 bg-white dark:bg-zinc-950 border-b md:border-b-0 md:border-r border-zinc-200 dark:border-zinc-900 flex md:flex-col p-4 md:p-6 shrink-0 gap-4 md:gap-0 overflow-x-auto md:overflow-visible no-scrollbar items-center md:items-stretch">
                        <div className="md:mb-8 shrink-0">
                            <h2 className="text-[16px] font-medium text-zinc-900 dark:text-zinc-100 leading-tight truncate max-w-[150px] md:max-w-none">{editFormData.name || 'Untitled Project'}</h2>
                        </div>
                        <div className="flex md:flex-col gap-2 md:gap-2 flex-1 md:space-y-2">
                            <div className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${editOnboardingStep === 1 ? 'bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800' : 'opacity-40'}`}>
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-medium border ${editOnboardingStep === 1 || editOnboardingStep > 1 ? 'bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-950 border-transparent' : 'bg-transparent text-zinc-400 border-zinc-300 dark:border-zinc-700'}`}>
                                    {editOnboardingStep > 1 ? <Check size={12} /> : 1}
                                </div>
                                <span className={`text-[13px] ${editOnboardingStep === 1 ? 'text-zinc-900 dark:text-zinc-100 font-medium' : 'text-zinc-500'} hidden md:inline`}>Details</span>
                            </div>
                            <div className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${editOnboardingStep === 2 ? 'bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800' : 'opacity-40'}`}>
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-medium border ${editOnboardingStep === 2 || editOnboardingStep > 2 ? 'bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-950 border-transparent' : 'bg-transparent text-zinc-400 border-zinc-300 dark:border-zinc-700'}`}>
                                    {editOnboardingStep > 2 ? <Check size={12} /> : 2}
                                </div>
                                <span className={`text-[13px] ${editOnboardingStep === 2 ? 'text-zinc-900 dark:text-zinc-100 font-medium' : 'text-zinc-500'} hidden md:inline`}>Questions</span>
                            </div>
                            <div className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${editOnboardingStep === 3 ? 'bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800' : 'opacity-40'}`}>
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-medium border ${editOnboardingStep === 3 || editOnboardingStep > 3 ? 'bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-950 border-transparent' : 'bg-transparent text-zinc-400 border-zinc-300 dark:border-zinc-700'}`}>
                                    {editOnboardingStep > 3 ? <Check size={12} /> : 3}
                                </div>
                                <span className={`text-[13px] ${editOnboardingStep === 3 ? 'text-zinc-900 dark:text-zinc-100 font-medium' : 'text-zinc-500'} hidden md:inline`}>Timeline</span>
                            </div>
                            <div className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${editOnboardingStep === 4 ? 'bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800' : 'opacity-40'}`}>
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-medium border ${editOnboardingStep === 4 ? 'bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-950 border-transparent' : 'bg-transparent text-zinc-400 border-zinc-300 dark:border-zinc-700'}`}>
                                    4
                                </div>
                                <span className={`text-[13px] ${editOnboardingStep === 4 ? 'text-zinc-900 dark:text-zinc-100 font-medium' : 'text-zinc-500'} hidden md:inline`}>Review</span>
                            </div>
                        </div>
                    </div>

                    <div className="flex-1 flex flex-col min-w-0 bg-zinc-50 dark:bg-black">
                        <div className="flex-1 overflow-y-auto p-6 md:p-8">
                            {editFormError && (
                                <div className="mb-4 text-[12px] text-amber-300 bg-amber-900/20 border border-amber-800 rounded px-3 py-2">
                                    {editFormError}
                                </div>
                            )}
                            {editOnboardingStep === 1 && (
                                <div className="space-y-6 max-w-lg mx-auto">
                                    <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100">Project Details</h3>
                                    <div className="space-y-4">
                                        <div className="space-y-1.5">
                                            <label className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">Name</label>
                                            <Input autoFocus placeholder="e.g. Q4 Rebrand" value={editFormData.name} onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })} />
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="space-y-1.5">
                                                <label className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">Receiver Name</label>
                                                <Input placeholder="Acme Inc." value={editFormData.receiverName} onChange={(e) => setEditFormData({ ...editFormData, receiverName: e.target.value })} />
                                            </div>
                                            <div className="space-y-1.5">
                                                <label className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">Receiver Email</label>
                                                <Input placeholder="contact@acme.com" value={editFormData.receiverEmail} onChange={(e) => setEditFormData({ ...editFormData, receiverEmail: e.target.value })} />
                                            </div>
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">Project Description</label>
                                            <TextArea
                                                className="h-24"
                                                value={editFormData.description}
                                                onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                                                placeholder="Welcome to your project console. This is your single source of truth for our progress, timeline, and deliveries."
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {editOnboardingStep === 2 && (
                                <div className="space-y-6 max-w-lg mx-auto">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100">Onboarding Questions</h3>
                                        <button
                                            onClick={() => handleEditOnboardingNext(true)}
                                            className="text-[11px] text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-300 transition-colors flex items-center gap-1 group"
                                        >
                                            Skip <ChevronRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
                                        </button>
                                    </div>

                                    <div>
                                        <p className="text-[13px] text-zinc-500 mb-6">Ask the receiver 1-3 essential questions before starting.</p>
                                        <div className="space-y-3">
                                            {editQuestions.map((q, idx) => (
                                                <div key={q.id} className="flex items-start gap-3 bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 p-3 rounded-md group">
                                                    <div className="flex-1 space-y-2">
                                                        <input className="w-full bg-transparent text-[13px] text-zinc-900 dark:text-zinc-100 focus:outline-none" value={q.text} onChange={(e) => { const nQ = [...editQuestions]; nQ[idx].text = e.target.value; setEditQuestions(nQ); }} />
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-[10px] text-zinc-500 uppercase">Required</span>
                                                            <Switch checked={q.required} onChange={(val) => { const nQ = [...editQuestions]; nQ[idx].required = val; setEditQuestions(nQ); }} />
                                                        </div>
                                                    </div>
                                                    <button onClick={() => setEditQuestions(prev => prev.filter((_, i) => i !== idx))} className="text-zinc-400 hover:text-red-500"><Trash2 size={14} /></button>
                                                </div>
                                            ))}
                                            {editQuestions.length < 3 && (
                                                <button onClick={() => setEditQuestions([...editQuestions, { id: Math.random().toString(), text: '', required: true }])} className="text-[12px] text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200 flex items-center gap-2">
                                                    <Plus size={14} /> Add Question
                                                </button>
                                            )}
                                        </div>
                                        <div className="mt-8 pt-4 border-t border-zinc-200 dark:border-zinc-900">
                                            <p className="flex items-center gap-2 text-[12px] text-zinc-500">
                                                <AlertCircle size={14} className="text-emerald-500" />
                                                Receiver answers will be used to finalize the project milestones.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {editOnboardingStep === 3 && (
                                <div className="space-y-6 max-w-xl mx-auto">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100">Milestone Timeline</h3>
                                        <Button size="sm" variant="secondary" onClick={async () => {
                                            setEditIsGenerating(true);
                                            const gen = await generateMilestones(editFormData.name);
                                            const status = editIncludeOnboarding ? MilestoneStatus.DRAFT : MilestoneStatus.NOT_STARTED;
                                            setEditMilestones(gen.map((m, idx) => ({
                                                ...m,
                                                status: (!editIncludeOnboarding && idx === 0) ? MilestoneStatus.IN_PROGRESS : status
                                            })));
                                            setEditIsGenerating(false);
                                        }}>
                                            <Sparkles size={14} className={`mr-2 ${editIsGenerating ? 'animate-spin' : ''}`} /> Generate
                                        </Button>
                                    </div>

                                    {editIncludeOnboarding ? (
                                        <div className="bg-zinc-100 dark:bg-zinc-900/30 border border-dashed border-zinc-300 dark:border-zinc-800 rounded p-3 mb-4 flex items-start gap-3">
                                            <div className="w-1.5 h-1.5 rounded-full bg-zinc-400 dark:bg-zinc-500 mt-1.5 shrink-0"></div>
                                            <div>
                                                <div className="text-[13px] font-medium text-zinc-700 dark:text-zinc-300">Draft — will be finalized after receiver onboarding</div>
                                                <div className="text-[11px] text-zinc-500 mt-1">Receivers will see these as a preview. You can adjust them after you receive their questionnaire answers.</div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/30 rounded p-3 mb-4 flex items-start gap-3">
                                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0"></div>
                                            <div>
                                                <div className="text-[13px] font-medium text-emerald-800 dark:text-emerald-100">Live Timeline</div>
                                                <div className="text-[11px] text-emerald-600 dark:text-emerald-500/80 mt-1">Receivers will see these milestones immediately upon joining.</div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="space-y-3">
                                        {editMilestones.map((m, idx) => (
                                            <div key={m.id} className="flex items-center gap-3 bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 p-3 rounded-md">
                                                <div className="flex-1 min-w-0">
                                                    <input className="bg-transparent text-[13px] text-zinc-900 dark:text-zinc-100 focus:outline-none w-full" value={m.title} onChange={(e) => { const nM = [...editMilestones]; nM[idx].title = e.target.value; setEditMilestones(nM); }} />
                                                </div>
                                                <div className="flex items-center gap-2 text-[11px] text-zinc-500 shrink-0">
                                                    <Clock size={12} />
                                                    <input type="number" className="w-5 bg-transparent text-center focus:outline-none" value={m.duration} onChange={(e) => { const nM = [...editMilestones]; nM[idx].duration = parseInt(e.target.value) || 0; setEditMilestones(nM); }} />d
                                                </div>
                                                <button onClick={() => setEditMilestones(prev => prev.filter(x => x.id !== m.id))} className="text-zinc-400 hover:text-red-500 shrink-0"><X size={14} /></button>
                                            </div>
                                        ))}
                                        <button onClick={() => {
                                            const status = editIncludeOnboarding ? MilestoneStatus.DRAFT : MilestoneStatus.NOT_STARTED;
                                            setEditMilestones([...editMilestones, { id: Math.random().toString(), title: "", status, duration: 3, comments: [] }])
                                        }} className="text-[12px] text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200 flex items-center gap-2">
                                            <Plus size={14} /> Add Milestone
                                        </button>
                                    </div>
                                </div>
                            )}

                            {editOnboardingStep === 4 && (
                                <div className="flex flex-col items-center justify-center h-full text-center max-w-lg mx-auto">
                                    <Rocket size={40} className="text-emerald-500 mb-4" />
                                    <h2 className="text-xl font-medium text-zinc-900 dark:text-zinc-100 mb-2">Ready to Resend Invite</h2>
                                    <p className="text-zinc-500 text-sm mb-8">Send updated invitation to {editFormData.receiverName}.</p>

                                    <div className="w-full bg-zinc-50 dark:bg-zinc-900/30 border border-zinc-200 dark:border-zinc-800 rounded-lg p-4 text-left mb-6">
                                        <div className="flex items-center justify-between mb-3">
                                            <span className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">Email Message</span>
                                            <div className="flex items-center gap-2">
                                                {editIsEditingEmail ? (
                                                    <Button size="sm" variant="secondary" className="h-6 text-[11px]" onClick={() => setEditIsEditingEmail(false)}>
                                                        Save
                                                    </Button>
                                                ) : (
                                                    <button
                                                        onClick={() => setEditIsEditingEmail(true)}
                                                        className="flex items-center gap-1.5 text-[11px] font-medium text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200 transition-colors px-2 py-1"
                                                    >
                                                        <Edit2 size={12} /> Edit
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {editIsEditingEmail ? (
                                            <textarea
                                                className="w-full h-32 p-3 text-[13px] text-zinc-600 dark:text-zinc-300 leading-relaxed font-mono bg-white dark:bg-zinc-950 rounded border border-zinc-200 dark:border-zinc-800 resize-none focus:outline-none focus:border-zinc-400 dark:focus:border-zinc-600"
                                                value={editEmailMessage}
                                                onChange={(e) => setEditEmailMessage(e.target.value)}
                                            />
                                        ) : (
                                            <div className="text-[13px] text-zinc-600 dark:text-zinc-300 leading-relaxed font-mono bg-white dark:bg-zinc-950 p-3 rounded border border-zinc-200 dark:border-zinc-800 whitespace-pre-wrap">
                                                {editEmailMessage}
                                            </div>
                                        )}
                                    </div>

                                    <div className="w-full flex items-center gap-4 mb-6">
                                        <div className="h-px bg-zinc-200 dark:bg-zinc-800 flex-1"></div>
                                        <span className="text-[11px] text-zinc-400 font-medium">OR SHARE LINK ONLY</span>
                                        <div className="h-px bg-zinc-200 dark:bg-zinc-800 flex-1"></div>
                                    </div>

                                    <div className="w-full">
                                        <div className="w-full bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 rounded p-2 flex items-center gap-2">
                                            <input readOnly value={editGeneratedLink} className="flex-1 bg-transparent text-[12px] text-zinc-600 dark:text-zinc-400 outline-none px-2 min-w-0" />
                                            <Button size="sm" variant="secondary" onClick={() => { navigator.clipboard.writeText(editGeneratedLink); alert("Link copied!"); }}><Copy size={12} /></Button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="p-5 border-t border-zinc-200 dark:border-zinc-900 bg-white dark:bg-zinc-950 flex justify-between items-center">
                            <span className="text-[11px] text-zinc-500">Step {editOnboardingStep} of 4</span>
                            <div className="flex gap-2">
                                {editOnboardingStep > 1 && <Button variant="ghost" onClick={() => setEditOnboardingStep(prev => (prev - 1) as 1 | 2 | 3 | 4)}>Back</Button>}
                                {editOnboardingStep < 4 ? (
                                    <Button
                                        onClick={() => handleEditOnboardingNext(false)}
                                        disabled={editOnboardingStep === 1 && (!editFormData.name.trim() || !editFormData.receiverName.trim() || !/\S+@\S+\.\S+/.test(editFormData.receiverEmail.trim()))}
                                    >
                                        Next
                                    </Button>
                                ) : (
                                    <Button onClick={handleEditOnboardingResend} variant="primary" disabled={!canResendInvite || isResendingInvite}>
                                        <Send size={14} className="mr-1.5" /> Resend Invite
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Share Modal (Manage Access) */}
            <Modal isOpen={isShareModalOpen} onClose={() => setIsShareModalOpen(false)} title="Manage Access">
                <div className="p-2 space-y-4">
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <h4 className="text-[13px] font-medium text-zinc-200">Public Link Access</h4>
                            <span className="text-[10px] text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20 font-medium tracking-wide">ENABLED</span>
                        </div>
                        <p className="text-[12px] text-zinc-500 mb-3 leading-relaxed">Anyone with this unique link can view the project timeline.</p>

                        <div className="flex gap-2">
                            <div className="flex-1 bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-[12px] text-zinc-400 truncate select-all font-mono">
                                https://verline.studio/onboard/{project.id}
                            </div>
                            <Button variant="secondary" onClick={() => alert("Link copied!")}>
                                <Copy size={14} strokeWidth={1.5} />
                            </Button>
                        </div>
                    </div>

                    <div className="border-t border-zinc-800 pt-4">
                        <Button className="w-full justify-between group text-[13px]" variant="secondary" onClick={() => { onOpenReceiverView && onOpenReceiverView(); setIsShareModalOpen(false); }}>
                            Open Receiver View <ArrowLeft size={14} strokeWidth={1.5} className="rotate-180 group-hover:translate-x-1 transition-transform" />
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Edit Project Modal (Provider) */}
            <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title="Edit Project Details">
                <div className="p-6 space-y-4">
                    <div className="space-y-1.5">
                        <label className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">Project Name</label>
                        <Input value={editName} onChange={(e) => setEditName(e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">Description</label>
                        <TextArea className="h-24" value={editDescription} onChange={(e) => setEditDescription(e.target.value)} />
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                        <Button variant="ghost" onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
                        <Button onClick={handleSaveEdit}>Save Changes</Button>
                    </div>
                </div>
            </Modal>

            {/* View Project Details Modal (Receiver) */}
            <Modal isOpen={isDetailsModalOpen} onClose={() => setIsDetailsModalOpen(false)} title="Project Details">
                <div className="p-6 space-y-6">
                    <div>
                        <h3 className="text-sm font-medium text-zinc-200 mb-1">{project.name}</h3>
                        <p className="text-[13px] text-zinc-500 leading-relaxed">{project.description || "No description provided."}</p>
                    </div>

                    {(project.onboardingAnswers || []).length > 0 && (
                        <div>
                            <div className="flex items-center gap-2 mb-3">
                                <h3 className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">Onboarding Answers</h3>
                                <span className="text-[10px] text-zinc-600 bg-zinc-900 px-1.5 py-0.5 rounded border border-zinc-800">Submitted</span>
                            </div>
                            <div className="space-y-4">
                                {project.onboardingAnswers?.map((ans, i) => (
                                    <div key={i} className="bg-zinc-900/50 border border-zinc-800 rounded p-3">
                                        <div className="text-[11px] text-zinc-400 mb-1 font-medium">{ans.questionText}</div>
                                        <div className="text-[13px] text-zinc-200">{ans.answerText}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end">
                        <Button variant="ghost" onClick={() => setIsDetailsModalOpen(false)}>Close</Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};